import { client, handleRequest, handleRequestBody } from "./client.ts";
import type { Construction } from "./constructions.ts";
import type { Stage, StageKind } from "./stages.ts";

export interface StageTemplate {
	id: string;
	name: string;
	description?: string;
	created_at: string;
	updated_at: string;
}

export interface StageTemplateItem {
	id: string;
	parent_item_id?: string;
	name: string;
	description?: string;
	kind: StageKind;
	position: number;
	depth?: number;
	created_at: string;
	updated_at: string;
}

export interface ListStageTemplatesParams {
	page?: number;
	pageSize?: number;
}

export interface ListStageTemplatesResponse {
	count: number;
	items: StageTemplate[];
}

const MAX_PAGE_SIZE = 100;

export function listStageTemplates(
	params: ListStageTemplatesParams = {},
): Promise<ListStageTemplatesResponse> {
	return handleRequestBody(() =>
		client.get<ListStageTemplatesResponse>("/stage-templates", {
			params: {
				page: params.page,
				page_size: params.pageSize ?? MAX_PAGE_SIZE,
			},
		}),
	);
}

export interface GetStageTemplateResponse {
	template: StageTemplate;
	items: StageTemplateItem[];
}

export function getStageTemplate(
	templateId: string,
): Promise<GetStageTemplateResponse> {
	return handleRequestBody(() =>
		client.get<GetStageTemplateResponse>(`/stage-templates/${templateId}`),
	);
}

interface StageTemplateResponse {
	template: StageTemplate;
}

export interface CreateStageTemplateInput {
	name: string;
	description?: string;
}

export async function createStageTemplate(
	input: CreateStageTemplateInput,
): Promise<StageTemplate> {
	const { template } = await handleRequestBody(() =>
		client.post<StageTemplateResponse>("/stage-templates", {
			name: input.name,
			description: input.description,
		}),
	);
	return template;
}

export interface UpdateStageTemplateInput {
	name?: string;
	description?: string;
}

export async function updateStageTemplate(
	templateId: string,
	input: UpdateStageTemplateInput,
): Promise<StageTemplate> {
	const { template } = await handleRequestBody(() =>
		client.patch<StageTemplateResponse>(`/stage-templates/${templateId}`, {
			name: input.name,
			description: input.description,
		}),
	);
	return template;
}

export async function deleteStageTemplate(templateId: string): Promise<void> {
	await handleRequest(() => client.delete(`/stage-templates/${templateId}`));
}

interface StageTemplateItemResponse {
	item: StageTemplateItem;
}

export interface CreateTemplateItemInput {
	name: string;
	description?: string;
	parentItemId?: string;
	position?: number;
	kind?: StageKind;
}

export async function createTemplateItem(
	templateId: string,
	input: CreateTemplateItemInput,
): Promise<StageTemplateItem> {
	const { item } = await handleRequestBody(() =>
		client.post<StageTemplateItemResponse>(
			`/stage-templates/${templateId}/items`,
			{
				name: input.name,
				description: input.description,
				parent_item_id: input.parentItemId,
				position: input.position,
				kind: input.kind,
			},
		),
	);
	return item;
}

export interface UpdateTemplateItemInput {
	name?: string;
	description?: string;
	position?: number;
	kind?: StageKind;
	parentItemId?: string;
}

export async function updateTemplateItem(
	templateId: string,
	itemId: string,
	input: UpdateTemplateItemInput,
): Promise<StageTemplateItem> {
	const { item } = await handleRequestBody(() =>
		client.patch<StageTemplateItemResponse>(
			`/stage-templates/${templateId}/items/${itemId}`,
			{
				name: input.name,
				description: input.description,
				position: input.position,
				kind: input.kind,
				parent_item_id: input.parentItemId,
			},
		),
	);
	return item;
}

export async function deleteTemplateItem(
	templateId: string,
	itemId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(`/stage-templates/${templateId}/items/${itemId}`),
	);
}

export interface TemplateFromConstructionInput {
	constructionId: string;
	name: string;
	description?: string;
}

export async function templateFromConstruction(
	input: TemplateFromConstructionInput,
): Promise<StageTemplate> {
	const { template } = await handleRequestBody(() =>
		client.post<GetStageTemplateResponse>(
			"/stage-templates/from-construction",
			{
				construction_id: input.constructionId,
				name: input.name,
				description: input.description,
			},
		),
	);
	return template;
}

export interface ApplyTemplateResponse {
	count: number;
	items: Stage[];
	construction: Construction;
}

export function applyTemplate(
	constructionId: string,
	templateId: string,
): Promise<ApplyTemplateResponse> {
	return handleRequestBody(() =>
		client.post<ApplyTemplateResponse>(
			`/constructions/${constructionId}/stages/apply-template`,
			{ template_id: templateId },
		),
	);
}
